import React from "react";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import { LatexText } from "../../../components/ui/latex-text";
import type { UnitNotes } from "../types";

const calculusAbUnit4Notes: UnitNotes = {
  unitId: "unit-4",
  unitName: "Unit 4: Contextual Applications of Differentiation",
  examWeight: "10–15%",
  focus: "Change, Contextual Applications",
  sections: [
    {
      id: "topic-4-1",
      title: "Topic 4.1: Interpreting the Meaning of the Derivative in Context",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Core Concept</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The derivative <LatexText text="$f'(x)$" /> represents the <strong>instantaneous rate of change</strong> of <LatexText text="$f(x)$" /> with respect to <LatexText text="$x$" />.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The "Nut Shell" Explanation</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              On the AP Exam (Free Response Questions), you are often asked to "interpret the meaning of the derivative in the context of the problem." Your answer must include three specific components:
            </p>
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Instantaneous</h4>
                  <p className="text-sm text-slate-600 mt-1">It is happening <em>at</em> a specific moment, not over an interval.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Context</h4>
                  <p className="text-sm text-slate-600 mt-1">You must reference the specific nouns in the problem (water, temperature, population), not just generic variables (<LatexText text="$x$" /> and <LatexText text="$y$" />).</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Units</h4>
                  <p className="text-sm text-slate-600 mt-1">The units of the derivative are always <LatexText text="$\frac{\text{Units of } f}{\text{Units of } x}$" />.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Template for Answering</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <p className="text-sm text-slate-700 mb-2"><strong>Prompt:</strong> Interpret <LatexText text="$W'(5) = 12$" /> where <LatexText text="$W(t)$" /> is amount of water in gallons and <LatexText text="$t$" /> is time in minutes.</p>
              <p className="text-sm text-slate-700"><strong>Response:</strong> "At time <LatexText text="$t=5$" /> minutes, the amount of water is <strong>increasing</strong> at a rate of 12 gallons per minute."</p>
            </div>
            <div className="space-y-2">
              <p className="text-slate-700"><strong>Positive Derivative:</strong> The quantity is increasing.</p>
              <p className="text-slate-700"><strong>Negative Derivative:</strong> The quantity is decreasing.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-2",
      title: "Topic 4.2: Straight-Line Motion (Rectilinear Motion)",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Hierarchy of Motion</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              When an object moves along a line (horizontal or vertical), we analyze three functions:
            </p>
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Position <LatexText text="$x(t)$" /> or <LatexText text="$s(t)$" /></h4>
                  <p className="text-sm text-slate-600 mt-1">Where the object is at time <LatexText text="$t$" />.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Velocity <LatexText text="$v(t) = s'(t)$" /></h4>
                  <p className="text-sm text-slate-600 mt-1">How fast it is moving and in what direction.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Acceleration <LatexText text="$a(t) = v'(t) = s''(t)$" /></h4>
                  <p className="text-sm text-slate-600 mt-1">The rate at which velocity is changing.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Interpreting Velocity</h3>
            <div className="space-y-2 mb-4">
              <p className="text-slate-700"><strong><LatexText text="$v(t) &gt; 0$" />:</strong> Object is moving right (or up).</p>
              <p className="text-slate-700"><strong><LatexText text="$v(t) &lt; 0$" />:</strong> Object is moving left (or down).</p>
              <p className="text-slate-700"><strong><LatexText text="$v(t) = 0$" />:</strong> Object is <strong>at rest</strong> (stopped).</p>
              <p className="text-slate-700"><strong>Direction Change:</strong> The object changes direction <em>only</em> when <LatexText text="$v(t)$" /> changes signs (crosses the x-axis).</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Speed vs. Velocity</h3>
            <div className="space-y-3 mb-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <p className="text-slate-700"><strong>Velocity</strong> is a vector (magnitude and direction).</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <p className="text-slate-700"><strong>Speed</strong> is a scalar (magnitude only).</p>
                <div className="mt-2">
                  <LatexText text="$$\text{Speed} = |v(t)|$$" />
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Speeding Up vs. Slowing Down (Crucial AP Topic)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              You cannot determine if an object is speeding up just by looking at acceleration. You must look at the relationship between <strong>velocity</strong> and <strong>acceleration</strong>.
            </p>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Speeding Up</h4>
                <p className="text-sm text-slate-700 mt-1"><LatexText text="$v(t)$" /> and <LatexText text="$a(t)$" /> have the <strong>SAME sign</strong> (both positive or both negative). They are working together.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Slowing Down</h4>
                <p className="text-sm text-slate-700 mt-1"><LatexText text="$v(t)$" /> and <LatexText text="$a(t)$" /> have <strong>OPPOSITE signs</strong>. They are working against each other.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-3",
      title: "Topic 4.3: Rates of Change in Applied Contexts Other Than Motion",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Generalizing Motion</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The logic of motion applies to anything that changes:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li>Population growth</li>
              <li>Temperature cooling</li>
              <li>Water draining from a tank</li>
              <li>Gravel being piled up</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">"Rate In" vs. "Rate Out"</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Many problems involve a quantity entering a system and leaving a system simultaneously (e.g., people entering and leaving an amusement park).
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              Let <LatexText text="$E(t)$" /> be the rate entering and <LatexText text="$L(t)$" /> be the rate leaving.
            </p>
            <div className="space-y-2">
              <p className="text-slate-700"><strong>Net Rate of Change:</strong> <LatexText text="$E(t) - L(t)$" />.</p>
              <p className="text-slate-700"><strong>Is the amount increasing?</strong> Check if <LatexText text="$E(t) > L(t)$" /> (Rate In {">"} Rate Out).</p>
              <p className="text-slate-700"><strong>Is the amount decreasing?</strong> Check if <LatexText text="$L(t) > E(t)$" /> (Rate Out {">"} Rate In).</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-4",
      title: "Topic 4.4: Introduction to Related Rates",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Concept</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Related Rates problems involve two or more variables that are changing with respect to <strong>time</strong> (<LatexText text="$t$" />). Because they are related by an equation (like the Pythagorean theorem or Volume formula), their <strong>rates of change</strong> are also related.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Implicit Differentiation with Respect to Time</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              In Unit 3, we differentiated <LatexText text="$y$" /> with respect to <LatexText text="$x$" /> (<LatexText text="$\frac{dy}{dx}$" />). In Related Rates, we differentiate <strong>everything</strong> with respect to <strong>time</strong> (<LatexText text="$t$" />).
            </p>
            <div className="space-y-2 mb-4">
              <p className="text-slate-700"><strong>Derivative of <LatexText text="$x^2$" />:</strong> <LatexText text="$2x \frac{dx}{dt}$" /></p>
              <p className="text-slate-700"><strong>Derivative of <LatexText text="$y^3$" />:</strong> <LatexText text="$3y^2 \frac{dy}{dt}$" /></p>
              <p className="text-slate-700"><strong>Derivative of <LatexText text="$r$" />:</strong> <LatexText text="$1 \frac{dr}{dt}$" /></p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Interpreting Notation</h3>
            <div className="space-y-2">
              <p className="text-slate-700"><strong><LatexText text="$\frac{dx}{dt}$" />:</strong> How fast the x-distance is changing.</p>
              <p className="text-slate-700"><strong><LatexText text="$\frac{dV}{dt}$" />:</strong> How fast the volume is changing.</p>
              <p className="text-slate-700"><strong><LatexText text="$\frac{d\theta}{dt}$" />:</strong> How fast the angle is changing (radians/time).</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-5",
      title: "Topic 4.5: Solving Related Rates Problems",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The 4-Step Procedure</h3>
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Draw a Picture</h4>
                  <p className="text-sm text-slate-600 mt-1">Label constants with numbers. Label changing quantities with variables (<LatexText text="$x$" />, <LatexText text="$y$" />, <LatexText text="$h$" />, <LatexText text="$\theta$" />).</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">List Knowns and Unknowns</h4>
                  <p className="text-sm text-slate-600 mt-1">Write down what you know (e.g., <LatexText text="$\frac{dx}{dt} = 5$" />) and what you need to find (e.g., find <LatexText text="$\frac{dy}{dt}$" /> when <LatexText text="$x=10$" />).</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Relate the Variables</h4>
                  <p className="text-sm text-slate-600 mt-1">Find a geometric equation that ties the variables together (Pythagorean theorem, Volume of Sphere, Area of Circle, Trig ratios).</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Differentiate and Solve</h4>
                  <p className="text-sm text-slate-600 mt-1">Differentiate the equation with respect to <LatexText text="$t$" /> (don't forget the Chain Rule!). Plug in the instantaneous values and solve for the unknown rate.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The "Static vs. Dynamic" Trap</h3>
            <ImportantNote>
              <strong>Never</strong> plug a number into the equation <em>before</em> differentiating unless that quantity is <strong>constant</strong> for the entire life of the problem (e.g., the length of a ladder never changes, so plug it in early. The water level in a draining cone changes, so keep it as variable <LatexText text="$h$" />).
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Common Problem Types</h3>
            <div className="space-y-2">
              <p className="text-slate-700"><strong>Ladder/Triangle:</strong> Uses <LatexText text="$x^2 + y^2 = c^2$" />.</p>
              <p className="text-slate-700"><strong>Shadows:</strong> Uses similar triangles (ratios of height/distance).</p>
              <p className="text-slate-700"><strong>Cones:</strong> Uses <LatexText text="$V = \frac{1}{3}\pi r^2 h$" />. Usually requires you to eliminate a variable (<LatexText text="$r$" /> or <LatexText text="$h$" />) using similar triangles before differentiating.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-6",
      title: "Topic 4.6: Approximating Values of a Function Using Local Linearity and Linearization",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Concept (Tangent Line Approximation)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Curves are hard to calculate; lines are easy. If you zoom in close enough to a smooth curve, it looks like a straight line. We use the <strong>tangent line</strong> at a known point to approximate function values near that point.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Linearization Formula</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To approximate <LatexText text="$f(x)$" /> near <LatexText text="$x=a$" />:
            </p>
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Find the point</h4>
                  <p className="text-sm text-slate-600 mt-1"><LatexText text="$(a, f(a))$" /></p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Find the slope</h4>
                  <p className="text-sm text-slate-600 mt-1"><LatexText text="$m = f'(a)$" /></p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Write the tangent line equation</h4>
                  <p className="text-sm text-slate-600 mt-1"><LatexText text="$y - f(a) = f'(a)(x - a)$" /></p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Solve for <LatexText text="$y$" /></h4>
                  <div className="mt-2">
                    <LatexText text="$$L(x) = f(a) + f'(a)(x-a)$$" />
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Overestimate vs. Underestimate</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Does the tangent line sit above or below the curve? This depends on <strong>concavity</strong> (the second derivative).
            </p>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Concave Up (<LatexText text="$f'' > 0$" />)</h4>
                <p className="text-sm text-slate-700 mt-1">Tangent lines sit <strong>below</strong> the curve. The approximation is an <strong>underestimate</strong>.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Concave Down (<LatexText text="$f'' < 0$" />)</h4>
                <p className="text-sm text-slate-700 mt-1">Tangent lines sit <strong>above</strong> the curve. The approximation is an <strong>overestimate</strong>.</p>
              </div>
            </div>

            <ImportantNote>
              <strong>AP Exam Tip:</strong> To justify an over/underestimate, you <em>must</em> reference the second derivative (<LatexText text="$f''$" />). Simply sketching it is not sufficient justification.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-7",
      title: "Topic 4.7: Using L'Hospital's Rule for Determining Limits of Indeterminate Forms",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Indeterminate Forms</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Sometimes, when evaluating a limit via direct substitution, you get a result that is meaningless, specifically:
            </p>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mb-4">
              <LatexText text="$$\frac{0}{0} \quad \text{or} \quad \frac{\infty}{\infty} \quad (\text{or } \pm \frac{\infty}{\infty})$$" />
              <p className="text-sm text-slate-700 mt-3">This does <strong>not</strong> mean the limit does not exist. It means we have more work to do.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">L'Hospital's Rule (Bernoulli's Rule)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If <LatexText text="$\lim_{x\to c} \frac{f(x)}{g(x)}$" /> yields <LatexText text="$\frac{0}{0}$" /> or <LatexText text="$\frac{\infty}{\infty}$" />, then:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <LatexText text="$$\lim_{x\to c} \frac{f(x)}{g(x)} = \lim_{x\to c} \frac{f'(x)}{g'(x)}$$" />
              <p className="text-sm text-slate-700 mt-3"><em>Note: This is NOT the Quotient Rule. You derive the numerator and denominator independently.</em></p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">AP Exam Syntax Requirement</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              On Free Response Questions, you must show that the conditions for L'Hospital's Rule are met. You cannot simply write <LatexText text="$= \frac{0}{0}$" />. That is mathematically "illegal" (0 cannot be divided by 0).
            </p>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-bold text-green-800 mb-3">Correct formatting:</h4>
              <ol className="list-decimal pl-5 space-y-2 text-sm text-slate-700">
                <li><LatexText text="$\lim_{x\to c} f(x) = 0$" /></li>
                <li><LatexText text="$\lim_{x\to c} g(x) = 0$" /></li>
                <li>"Therefore, by L'Hospital's Rule..."</li>
                <li><LatexText text="$\lim_{x\to c} \frac{f'(x)}{g'(x)} = \dots$" /></li>
              </ol>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Repeated Applications</h3>
            <p className="text-slate-700 leading-relaxed">
              Sometimes applying the rule once yields <LatexText text="$\frac{0}{0}$" /> again. You can apply L'Hospital's Rule a second (or third) time until you get a determinate answer.
            </p>
          </section>
        </div>
      )
    }
  ],
};

export default calculusAbUnit4Notes;